import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import type { CheckboxTreeNode } from 'components/CheckboxTreeList/CheckboxTreeList';
import type { MetricType } from 'components/MetricsDashboard/types';
import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import { CSRFService } from 'services/csrf';
import { MetricsService } from 'services/metrics';
import type { StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';

import type {
  ChartView,
  ChartViewChartSummaryCard,
  ChartViewCloneRequest,
  ChartViewCreateRequest,
  ChartViewCreateResponse,
  ChartViewDetailResponse,
  ChartViewListItem,
  ChartViewUpdateRequest,
  ChartViewsListResponse,
} from 'components/Analytics/ChartViews/ChartViewTypes';

const chartViewsBaseUrl = '/api/v1/spm/chart-views/';

export const getChartViewsListQueryKey = (hierarchyModelId: StringifiedUUID) =>
  ['hierarchy-models', hierarchyModelId, 'chart-views', 'list'] as const;

/**
 * Query key for one chart view's detail, or the prefix for all detail caches under the hierarchy when
 * `chartViewId` is omitted.
 */
export const getChartViewDetailQueryKey = (hierarchyModelId: StringifiedUUID, chartViewId?: StringifiedUUID | null) =>
  chartViewId != null
    ? (['hierarchy-models', hierarchyModelId, 'chart-views', chartViewId] as const)
    : (['hierarchy-models', hierarchyModelId, 'chart-views'] as const);

export const listChartViews = (hierarchyModelId: StringifiedUUID) =>
  getRequest<Record<string, string>, ChartViewsListResponse>(chartViewsBaseUrl, {
    hierarchy_model: hierarchyModelId,
  });

export const getChartViewDetail = (chartViewId: StringifiedUUID) =>
  getRequest<null, ChartViewDetailResponse>(`${chartViewsBaseUrl}${chartViewId}/`);

export const mapDetailToChartView = (detail: ChartViewDetailResponse): ChartView => ({
  id: detail.id,
  viewName: detail.view_name,
  assignedTerritoryIds: detail.territory_uuids,
  charts: detail.charts.map(
    (c): ChartViewChartSummaryCard => ({
      id: c.id,
      title: c.title,
      metricType: c.metric_type as MetricType,
    }),
  ),
});

export const mapListItemToChartViewStub = (item: ChartViewListItem): ChartView => ({
  id: item.id,
  viewName: item.view_name,
  assignedTerritoryIds: [],
  charts: [],
});

/** Leaf territory ids in tree order (same rule as `CheckboxTreeList` `getAllLeafIds`). */
function collectLeafTerritoryIds(nodes: CheckboxTreeNode[]): StringifiedUUID[] {
  const out: StringifiedUUID[] = [];
  const visit = (node: CheckboxTreeNode) => {
    if (!node.children || node.children.length === 0) {
      out.push(node.id as StringifiedUUID);
      return;
    }
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return out;
}

export function firstSelectableTerritoryId(nodes: CheckboxTreeNode[]): StringifiedUUID | null {
  const leaves = collectLeafTerritoryIds(nodes);
  return leaves[0] ?? null;
}

/** Subset of list payload used by Chart Views modal (smaller `data` referential surface with `select`). */
export type ChartViewsModalListData = Pick<ChartViewsListResponse, 'results'>;

export const chartViewsModalListSelect = (data: ChartViewsListResponse): ChartViewsModalListData => ({
  results: data.results,
});

export type UseChartViewsListQueryOptions<TData = ChartViewsListResponse> = Omit<
  UseQueryOptions<ChartViewsListResponse, APIError, TData>,
  'queryKey' | 'queryFn'
>;

export type UseChartViewDetailQueryOptions = Omit<
  UseQueryOptions<ChartViewDetailResponse, APIError>,
  'queryKey' | 'queryFn'
>;

const useChartViewsListQuery = <TData = ChartViewsListResponse>(
  hierarchyModelId: StringifiedUUID,
  options: UseChartViewsListQueryOptions<TData> = {},
) => {
  const queryKey = getChartViewsListQueryKey(hierarchyModelId);
  return useQuery<ChartViewsListResponse, APIError, TData>(
    queryKey,
    () => listChartViews(hierarchyModelId as StringifiedUUID),
    {
      ...options,
      enabled: options.enabled !== false,
    },
  );
};

const useChartViewDetailQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  chartViewId: StringifiedUUID | null | undefined,
  options: UseChartViewDetailQueryOptions = {},
) => {
  const queryKey =
    hierarchyModelId && chartViewId
      ? getChartViewDetailQueryKey(hierarchyModelId, chartViewId)
      : (['hierarchy-models', 'chart-views', 'detail-disabled'] as const);
  return useQuery<ChartViewDetailResponse, APIError>(
    queryKey,
    () => getChartViewDetail(chartViewId as StringifiedUUID),
    {
      ...options,
      enabled: !!hierarchyModelId && !!chartViewId && options.enabled !== false,
    },
  );
};

const useCreateChartViewMutation = (
  hierarchyModelId: StringifiedUUID,
  options: UseMutationOptions<ChartViewCreateResponse, APIError, ChartViewCreateRequest> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ChartViewCreateResponse, APIError, ChartViewCreateRequest>(
    (body) => postRequest<ChartViewCreateRequest, ChartViewCreateResponse>(chartViewsBaseUrl, body, csrfToken),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (hierarchyModelId) {
          queryClient.invalidateQueries(getChartViewDetailQueryKey(hierarchyModelId, data.id));
          queryClient.invalidateQueries(getChartViewsListQueryKey(hierarchyModelId));
          queryClient.invalidateQueries(['hierarchy-models', hierarchyModelId, 'territory-objects']);
        }
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

const useUpdateChartViewMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  options: UseMutationOptions<
    ChartViewDetailResponse,
    APIError,
    { chartViewId: StringifiedUUID; body: ChartViewUpdateRequest }
  > = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ChartViewDetailResponse, APIError, { chartViewId: StringifiedUUID; body: ChartViewUpdateRequest }>(
    ({ chartViewId, body }) =>
      putRequest<ChartViewUpdateRequest, ChartViewDetailResponse>(
        `${chartViewsBaseUrl}${chartViewId}/`,
        body,
        csrfToken,
      ),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (hierarchyModelId) {
          queryClient.invalidateQueries(getChartViewDetailQueryKey(hierarchyModelId, variables.chartViewId));
          queryClient.invalidateQueries(getChartViewsListQueryKey(hierarchyModelId));
        }
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

const useDeleteChartViewMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  options: UseMutationOptions<null, APIError, { chartViewId: StringifiedUUID }> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<null, APIError, { chartViewId: StringifiedUUID }>(
    ({ chartViewId }) => deleteRequest<null, null>(`${chartViewsBaseUrl}${chartViewId}/`, null, csrfToken),
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        if (hierarchyModelId) {
          queryClient.invalidateQueries(getChartViewsListQueryKey(hierarchyModelId));
          queryClient.removeQueries(getChartViewDetailQueryKey(hierarchyModelId, variables.chartViewId));
          queryClient.invalidateQueries(['hierarchy-models', hierarchyModelId, 'territory-objects']);
          await MetricsService.invalidateMetricConfigsQueriesForHierarchyModel(queryClient, hierarchyModelId);
        }
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

const useCloneChartViewMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  options: UseMutationOptions<
    ChartViewCreateResponse,
    APIError,
    { sourceViewId: StringifiedUUID; body: ChartViewCloneRequest }
  > = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ChartViewCreateResponse, APIError, { sourceViewId: StringifiedUUID; body: ChartViewCloneRequest }>(
    ({ sourceViewId, body }) =>
      postRequest<ChartViewCloneRequest, ChartViewCreateResponse>(
        `${chartViewsBaseUrl}${sourceViewId}/clone/`,
        body,
        csrfToken,
      ),
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        if (hierarchyModelId) {
          queryClient.invalidateQueries(getChartViewsListQueryKey(hierarchyModelId));
          queryClient.invalidateQueries(['hierarchy-models', hierarchyModelId, 'territory-objects']);
          await MetricsService.invalidateMetricConfigsQueriesForHierarchyModel(queryClient, hierarchyModelId);
        }
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

export const ChartViewsService = {
  chartViewsBaseUrl,
  getChartViewsListQueryKey,
  getChartViewDetailQueryKey,
  listChartViews,
  getChartViewDetail,
  mapDetailToChartView,
  mapListItemToChartViewStub,
  firstSelectableTerritoryId,
  chartViewsModalListSelect,
  useChartViewsListQuery,
  useChartViewDetailQuery,
  useCreateChartViewMutation,
  useUpdateChartViewMutation,
  useDeleteChartViewMutation,
  useCloneChartViewMutation,
};
